import { defineStore } from "pinia";
import Utils from "@/services/utils";
import { constants } from "@/constants";
import MultiLocalStorage from "@/services/multiLocalStorage";

export const useDraftStore = defineStore("draft", {
  state: () => ({
    _selectedChoice: null,
    _selectedPaymentMethod: null,
    _selectedCountryCode: null,
    _sendingDraftOrder: null, // integer used by setTimeout
    _cgvApproved: false,
    _cgsApproved: false,
    _formValid: false,
    _order: { externalOrderId: Utils.generateUID() },
    _prefillLoading: false,
    _aspspEligibilityLoading: false,
    _isEligibleToOneClick: false,
    _fullAddress: "",
    _draftOrderSent: false,
    _actionLegacy: "create",
    _has1ClickOptOut: false,
    _isDistinctAdress: false,
    _isBillingFormCompleted: false,
    _initialOrderData: null,
    _isOneClickProcessing: false,
    _billingEntityId: null,
    _billingEntityName: null,
    _termsAndConditionsUrl: window?.globalConfig?.termsAndConditionsUrl || null,
  }),
  getters: {
    selectedChoice: (state) => {
      return state._selectedChoice;
    },
    selectedPaymentMethod: (state) => {
      return state._selectedPaymentMethod;
    },
    selectedCountryCode: (state) => {
      return state._selectedCountryCode;
    },
    isSendingDraftOrder: (state) => {
      return state._sendingDraftOrder;
    },
    areCgvApproved: (state) => {
      return state._cgvApproved;
    },
    areCgsApproved: (state) => {
      return state._cgsApproved;
    },
    isFormValid: (state) => {
      return state._formValid;
    },
    order: (state) => {
      return state._order;
    },
    isPrefillLoading: (state) => {
      return state._prefillLoading;
    },
    isAspspEligibilityLoading: (state) => {
      return state._aspspEligibilityLoading;
    },
    isEligibleToOneClick: (state) => {
      return state._isEligibleToOneClick;
    },
    fullAddress: (state) => {
      return state._fullAddress;
    },
    isDraftOrderSent: (state) => {
      return state._draftOrderSent;
    },
    actionLegacy: (state) => {
      return state._actionLegacy;
    },
    has1ClickOptOut: (state) => {
      if (!state._has1ClickOptOut) {
        state._has1ClickOptOut =
          localStorage.getItem(constants.HAS_1_CLICK_OPTOUT) === "true";
      }

      return state._has1ClickOptOut;
    },
    isDistinctAdress: (state) => {
      return state._isDistinctAdress;
    },
    initialOrderData: (state) => {
      if (!state._initialOrderData) {
        state._initialOrderData = MultiLocalStorage.get(
          constants.INITIAL_ORDER_DATA,
          window?.globalConfig?.domainId,
        );
      }

      return state._initialOrderData;
    },
    isOneClickProcessing: (state) => {
      return state._isOneClickProcessing;
    },
    billingEntityId: (state) => {
      return state._billingEntityId;
    },
    billingEntityName: (state) => {
      return state._billingEntityName;
    },
    termsAndConditionsUrl: (state) => {
      return state._termsAndConditionsUrl;
    },
  },
  actions: {
    selectChoice(choice) {
      this._selectedChoice = choice;
    },
    selectPaymentMethod(paymentMethod) {
      this._selectedPaymentMethod = paymentMethod;
    },
    selectCountryCode(countryCode) {
      this._selectedCountryCode = countryCode;
    },
    sendingDraftOrder(sending) {
      this._sendingDraftOrder = sending;
    },
    approveCgv(approved) {
      this._cgvApproved = approved;
    },
    approveCgs(approved) {
      this._cgsApproved = approved;
    },
    formValid(valid) {
      this._formValid = valid;
    },
    updateOrder(order) {
      Object.assign(this._order, order);
    },
    prefillLoading(loading) {
      this._prefillLoading = loading;
    },
    aspspEligibilityLoading(loading) {
      this._aspspEligibilityLoading = loading;
    },
    eligibleToOneClick(eligible) {
      this._isEligibleToOneClick = eligible;
    },
    updateFullAddress(address) {
      this._fullAddress = address;
    },
    draftOrderSent(sent) {
      this._draftOrderSent = sent;
    },
    updateActionLegacy(action) {
      this._actionLegacy = action;
    },
    updateHas1ClickOptOut(has1ClickOptOut) {
      localStorage.setItem(constants.HAS_1_CLICK_OPTOUT, has1ClickOptOut);
      this._has1ClickOptOut = has1ClickOptOut;
    },
    updateIsDistinctAdress(isDistinctAdress) {
      this._isDistinctAdress = isDistinctAdress;
    },
    updateBillingFormCompletion(isCompleted) {
      this._isBillingFormCompleted = isCompleted;
    },
    updateInitialOrderData(externalOrderId, paymentType) {
      MultiLocalStorage.set(
        constants.INITIAL_ORDER_DATA,
        window?.globalConfig?.domainId,
        { externalOrderId, paymentType },
      );

      this._initialOrderData = { externalOrderId, paymentType };
    },
    setOneClickProcessing(processing) {
      this._isOneClickProcessing = processing;
    },
    updateBillingEntity(sfId, name) {
      this._billingEntityId = sfId;
      this._billingEntityName = name;
    },
    resetBillingEntity() {
      this._billingEntityId = null;
      this._billingEntityName = null;
      this._termsAndConditionsUrl =
        window?.globalConfig?.termsAndConditionsUrl || null;
    },
    updateTermsUrlForEntity(sfId) {
      const jc = window?.globalConfig?.journeyConfig;
      const config = jc
        ? jc.billingEntityConfig
        : window?.globalConfig?.billingEntityConfig;
      if (!config?.entities || !sfId) return;
      const entity = Object.values(config.entities).find(
        (e) => e.sfId === sfId,
      );
      this._termsAndConditionsUrl =
        entity?.termsAndConditionsUrl ||
        window?.globalConfig?.termsAndConditionsUrl ||
        null;
    },
  },
});
